import { useCallback, useEffect, useState } from 'react'
import { Button, Chip, Input, Switch, ToggleButton, ToggleButtonGroup } from '@heroui/react'
import { api } from '../lib/api'
import {
  addRange,
  bookingWithoutHours,
  branchProblem,
  clockOf,
  minutesOf,
  serviceProblem,
  sortedHours,
  startingHours,
  toggleWeekday,
} from '../lib/bookingAdmin'
import { dayName, nameIn, type StaffKey } from '../lib/staffText'
import type { BookingBranchView, BookingServiceRow, Branch, DayHours } from '../lib/types'
import { useUnsaved } from '../lib/unsaved'
import { Icon } from '../ui/icons'
import { StatusChip } from '../ui/parts'
import { useStaffText } from '../ui/StaffText'
import { Messages, useAction } from './parts'
import { EditLayout, FieldLabel, FormSection, IconTile, Notice, PageHead, SaveActions, SummaryCard, SwitchRow, Tick, UnitNum } from './settingsKit'
import '../styles/branchpage.css'
import '../styles/admin.css'

/** Monday first, Sunday last (0 is Sunday). */
const WEEK = [1, 2, 3, 4, 5, 6, 0] as const
const dayLong = (d: number) => `admin.day.${d}` as StaffKey
const dayShort = (d: number) => `org.day.${d}` as StaffKey

const same = (a: unknown, b: unknown) => JSON.stringify(a) === JSON.stringify(b)

/**
 * Administration → Online booking → Branches, for the branch in the top bar: an
 * edit page. The switch, how far ahead, address and booking hours are one draft
 * saved from the head; each service saves on its own; closed dates at once.
 * Without the branch's seat the switch is shown off and disabled.
 */
export function BookingBranches({ branch }: { branch: Pick<Branch, 'id' | 'code' | 'name' | 'openingHours'> }) {
  const branchId = branch.id
  const { lang, say } = useStaffText()
  const [view, setView] = useState<BookingBranchView | null>(null)
  const [saved, setSaved] = useState<BookingBranchView | null>(null)
  const [day, setDay] = useState('')
  const [label, setLabel] = useState('')

  const load = useCallback(async () => {
    const answer = await api.bookingBranch(branchId)
    setView(answer)
    setSaved(answer)
  }, [branchId])
  const { busy, error, notice, run, setError } = useAction(load)
  useEffect(() => {
    load().catch(() => setView(null))
  }, [load])

  const savedRow = (id: string) => saved?.services.find((r) => r.serviceId === id)
  const settingsDirty = Boolean(view && saved && !same(view.settings, saved.settings))
  const rowsDirty = Boolean(view?.services.some((r) => !same(r, savedRow(r.serviceId))))
  useUnsaved(settingsDirty || rowsDirty)

  if (!view) return <PageHead title={say('booking.section.branches')} />
  const s = view.settings
  const setSettings = (next: Partial<typeof s>) => setView({ ...view, settings: { ...s, ...next } })
  const setRow = (id: string, next: Partial<BookingServiceRow>) =>
    setView({ ...view, services: view.services.map((r) => (r.serviceId === id ? { ...r, ...next } : r)) })

  async function saveBranch() {
    const problem = branchProblem(s)
    if (problem) return setError(say(problem))
    await run(() => api.saveBookingBranch(branchId, s), say('common.saved'))
  }
  async function saveRow(row: BookingServiceRow) {
    const problem = serviceProblem(row)
    if (problem) return setError(say(problem))
    await run(() => api.saveBookingService(branchId, row), say('common.saved'))
  }
  // A save reloads the whole view; the edits it did not save are carried over the reload.
  const keepOthers = async (fn: () => Promise<void>, saving: { settings?: boolean; serviceId?: string } = {}) => {
    const pending = view.services.filter((r) => r.serviceId !== saving.serviceId && !same(r, savedRow(r.serviceId)))
    const draft = settingsDirty && !saving.settings ? s : null
    await fn()
    setView((v) =>
      v && {
        ...v,
        settings: draft ?? v.settings,
        services: v.services.map((r) => pending.find((p) => p.serviceId === r.serviceId) ?? r),
      },
    )
  }

  const on = s.enabled && view.seat
  const online = view.services.filter((r) => r.offered && r.enabled).length

  return (
    <>
      <PageHead
        title={say('booking.section.branches')}
        line={say('booking.line.branches', { branch: branch.name })}
        actions={
          <SaveActions
            dirty={settingsDirty}
            busy={busy}
            onDiscard={() => saved && setView({ ...view, settings: saved.settings })}
            onSave={() => void keepOthers(saveBranch, { settings: true })}
          />
        }
      />
      <Messages error={error} notice={notice} />
      {bookingWithoutHours(on, s.hours ?? branch.openingHours) && (
        <Notice tone="warning" className="bp-notice">
          {say('booking.noHours')}{' '}
          <a className="linkish" href={`#branches/${encodeURIComponent(branch.code)}`}>
            {say('booking.setHours')}
          </a>
        </Notice>
      )}

      <EditLayout
        side={
          <SummaryCard
            band={say('booking.group')}
            tile={<IconTile icon="booking" size="xl" />}
            name={branch.name}
            sub={branch.code}
            state={<StatusChip tone={on ? 'ok' : undefined}>{say(on ? 'connect.on' : 'connect.off')}</StatusChip>}
            stats={[
              { label: say('booking.br.statServices'), value: online },
              { label: say('booking.br.statAhead'), value: s.daysAhead },
              { label: say('booking.br.statClosed'), value: view.closed.length },
            ]}
          />
        }
      >
        <FormSection id="bb-settings" icon="booking" title={say('booking.group')} line={say('booking.br.settingsLine')}>
          <div className="bb-rows">
            <SwitchRow
              label={say('booking.br.takes')}
              status={view.seat ? undefined : say('booking.noSeat')}
              checked={on}
              disabled={!view.seat}
              onChange={(enabled) => setSettings({ enabled })}
            />
          </div>
          <div className="bb-grid">
            <label className="field">
              <FieldLabel text={say('booking.br.daysAhead')} />
              <UnitNum
                label={say('booking.daysAhead')}
                value={s.daysAhead}
                min={1}
                max={90}
                unit={say('booking.unit.days')}
                onChange={(v) => setSettings({ daysAhead: v ?? 0 })}
              />
            </label>
            <label className="field">
              <FieldLabel text={say('booking.br.notice')} />
              <UnitNum
                label={say('booking.notice')}
                value={s.minNoticeMin}
                min={0}
                max={1440}
                unit={say('booking.unit.min')}
                onChange={(v) => setSettings({ minNoticeMin: v ?? 0 })}
              />
            </label>
            <label className="field bb-grid__wide">
              <FieldLabel text={say('booking.address')} optional />
              <Input fullWidth value={s.address} onChange={(e) => setSettings({ address: e.target.value })} />
            </label>
          </div>
        </FormSection>

        <FormSection id="bb-hours" icon="clock" title={say('booking.hours')} line={say('booking.br.hoursLine')}>
          <div className="bb-rows">
            <SwitchRow
              label={say('booking.ownHours')}
              status={s.hours === null ? say('booking.followsOpening') : undefined}
              checked={s.hours !== null}
              onChange={(own) => setSettings({ hours: own ? startingHours(branch.openingHours) : null })}
            />
          </div>
          {s.hours !== null && (
            <BookingHours hours={s.hours} opening={branch.openingHours ?? []} onChange={(hours) => setSettings({ hours })} />
          )}
        </FormSection>

        <FormSection
          id="bb-services"
          icon="grid"
          title={say('booking.services')}
          line={say('booking.br.servicesLine')}
          aside={<span className="set-muted">{say('booking.br.onlineCount', { n: online, of: view.services.length })}</span>}
        >
          <ul className="bb-svcs">
            {view.services.map((row) => (
              <ServiceRow
                key={row.serviceId}
                row={row}
                name={nameIn(row.name, lang, row.code || row.serviceId)}
                daysAhead={s.daysAhead}
                minNotice={s.minNoticeMin}
                dirty={!same(row, savedRow(row.serviceId))}
                busy={busy}
                onChange={(next) => setRow(row.serviceId, next)}
                onSave={() => void keepOthers(() => saveRow(row), { serviceId: row.serviceId })}
              />
            ))}
          </ul>
        </FormSection>

        <FormSection id="bb-closed" icon="pause" title={say('booking.closed')} line={say('booking.br.closedLine')}>
          {view.closed.length > 0 && (
            <ul className="br-dates">
              {view.closed.map((d) => (
                <li key={d.day} className="br-date">
                  <span>{dayName(d.day, lang)}</span>
                  {d.label && <span className="br-date__label">{d.label}</span>}
                  {d.booked > 0 && (
                    <Chip size="sm" variant="soft" color="warning">
                      {say('booking.closedBooked', { n: d.booked })}
                    </Chip>
                  )}
                  <Button
                    isIconOnly
                    variant="ghost"
                    size="sm"
                    aria-label={say('common.remove')}
                    onPress={() => void keepOthers(() => run(() => api.removeClosedDay(branchId, d.day), say('common.saved')))}
                  >
                    <Icon name="close" size={16} />
                  </Button>
                </li>
              ))}
            </ul>
          )}
          <div className="br-adddate">
            <label className="field">
              <span>{say('booking.day')}</span>
              <Input type="date" value={day} onChange={(e) => setDay(e.target.value)} />
            </label>
            <label className="field">
              <span>{say('booking.closedLabel')}</span>
              <Input value={label} onChange={(e) => setLabel(e.target.value)} />
            </label>
            <Button
              variant="secondary"
              isDisabled={!day || busy}
              onPress={() =>
                void keepOthers(() =>
                  run(async () => {
                    await api.addClosedDay(branchId, day, label.trim())
                    setDay('')
                    setLabel('')
                  }, say('common.saved')),
                )
              }
            >
              <Icon name="plus" size={16} /> {say('booking.addDate')}
            </Button>
          </div>
        </FormSection>
      </EditLayout>
    </>
  )
}

/** One service: its Online switch, and its slots and limits while it is booked online. */
function ServiceRow({
  row,
  name,
  daysAhead,
  minNotice,
  dirty,
  busy,
  onChange,
  onSave,
}: {
  row: BookingServiceRow
  name: string
  daysAhead: number
  minNotice: number
  dirty: boolean
  busy: boolean
  onChange: (next: Partial<BookingServiceRow>) => void
  onSave: () => void
}) {
  const { say } = useStaffText()
  return (
    <li className="bb-svc" data-off={!row.offered || !row.enabled || undefined} aria-label={name}>
      <div className="bb-svc__head">
        <span className="bb-svc__name">
          {name}
          {!row.offered && (
            <Chip size="sm" variant="secondary">
              {say('booking.notRouted')}
            </Chip>
          )}
        </span>
        <Switch
          className="bb-svc__switch"
          aria-label={say('booking.online')}
          isSelected={row.enabled}
          isDisabled={!row.offered}
          onChange={(enabled) => onChange({ enabled })}
        >
          <Switch.Content className="sw-hit">
            <span>{say('booking.online')}</span>
            <Switch.Control>
              <Switch.Thumb />
            </Switch.Control>
          </Switch.Content>
        </Switch>
        <Button variant={dirty ? 'primary' : 'secondary'} size="sm" isDisabled={busy || !dirty} onPress={onSave}>
          {say('common.save')}
        </Button>
      </div>
      {row.enabled && (
        <div className="bb-svc__body">
          <div className="bb-grid bb-grid--3">
            <label className="field">
              <FieldLabel text={say('booking.br.slot')} />
              <UnitNum
                label={say('booking.slotMin')}
                value={row.slotMin}
                min={5}
                max={240}
                unit={say('booking.unit.min')}
                onChange={(v) => onChange({ slotMin: v ?? 0 })}
              />
            </label>
            <label className="field">
              <FieldLabel text={say('booking.perSlot')} />
              <UnitNum label={say('booking.perSlot')} value={row.perSlot} min={1} max={50} onChange={(v) => onChange({ perSlot: v ?? 0 })} />
            </label>
            <label className="field">
              <FieldLabel text={say('booking.dailyMax')} optional />
              <UnitNum
                label={say('booking.dailyMax')}
                value={row.dailyMax}
                min={1}
                max={5000}
                empty={say('booking.noCap')}
                onChange={(dailyMax) => onChange({ dailyMax })}
              />
            </label>
            <label className="field">
              <FieldLabel text={say('booking.br.daysAhead')} optional />
              <UnitNum
                label={say('booking.daysAhead')}
                value={row.daysAhead}
                min={1}
                max={90}
                unit={say('booking.unit.days')}
                empty={String(daysAhead)}
                onChange={(next) => onChange({ daysAhead: next })}
              />
            </label>
            <label className="field">
              <FieldLabel text={say('booking.br.notice')} optional />
              <UnitNum
                label={say('booking.notice')}
                value={row.minNoticeMin}
                min={0}
                max={1440}
                unit={say('booking.unit.min')}
                empty={String(minNotice)}
                onChange={(minNoticeMin) => onChange({ minNoticeMin })}
              />
            </label>
          </div>
          <fieldset className="field">
            <legend>{say('booking.weekdays')}</legend>
            <ToggleButtonGroup
              className="set-seg"
              aria-label={say('booking.weekdays')}
              size="sm"
              selectionMode="multiple"
              selectedKeys={(row.weekdays ?? []).map(String)}
              onSelectionChange={(keys) => {
                const next = WEEK.filter((d) => keys.has(String(d)))
                const was = row.weekdays ?? []
                const flipped = WEEK.find((d) => next.includes(d) !== was.includes(d))
                if (flipped !== undefined) onChange({ weekdays: toggleWeekday(was, flipped) })
              }}
            >
              {WEEK.map((d, i) => (
                <ToggleButton key={d} id={String(d)} aria-label={say(dayLong(d))}>
                  {i > 0 && <ToggleButtonGroup.Separator />}
                  {say(dayShort(d))}
                </ToggleButton>
              ))}
            </ToggleButtonGroup>
          </fieldset>
        </div>
      )}
    </li>
  )
}

/** 24:00 is written 00:00 in a time input and read back as the day's end. */
const timeOf = (min: number) => (min >= 1440 ? '00:00' : clockOf(min))

/** A branch's booking hours: a row per weekday, each with one or more ranges. */
function BookingHours({ hours, opening, onChange }: { hours: DayHours[]; opening: DayHours[]; onChange: (h: DayHours[]) => void }) {
  const { say } = useStaffText()
  const replace = (from: DayHours, to: DayHours) => onChange(sortedHours(hours.map((h) => (h === from ? to : h))))
  return (
    <ul className="br-days bb-hours">
      {WEEK.map((day) => {
        const ranges = sortedHours(hours.filter((h) => h.day === day))
        const name = say(dayLong(day))
        return (
          <li key={day} className="br-day" data-closed={ranges.length === 0 || undefined}>
            <Tick
              className="br-day__name"
              checked={ranges.length > 0}
              onChange={() => onChange(ranges.length > 0 ? hours.filter((h) => h.day !== day) : addRange(hours, day, opening))}
            >
              {name}
            </Tick>
            {ranges.length === 0 ? (
              <span className="br-day__closed">{say('common.desk.closed')}</span>
            ) : (
              <span className="bb-ranges">
                {ranges.map((r, i) => (
                  <span key={i} className="br-day__times">
                    <Input
                      type="time"
                      aria-label={say('org.br.opensAria', { day: name })}
                      value={timeOf(r.open)}
                      onChange={(e) => replace(r, { ...r, open: minutesOf(e.target.value) ?? r.open })}
                    />
                    <span className="muted">{say('admin.hours.to')}</span>
                    <Input
                      type="time"
                      aria-label={say('org.br.closesAria', { day: name })}
                      aria-invalid={r.close <= r.open || undefined}
                      value={timeOf(r.close)}
                      onChange={(e) => replace(r, { ...r, close: minutesOf(e.target.value) || 1440 })}
                    />
                    <Button isIconOnly variant="ghost" size="sm" aria-label={say('common.remove')} onPress={() => onChange(hours.filter((h) => h !== r))}>
                      <Icon name="close" size={14} />
                    </Button>
                  </span>
                ))}
                <Button
                  isIconOnly
                  variant="ghost"
                  size="sm"
                  aria-label={say('booking.addRange', { day: name })}
                  onPress={() => onChange(addRange(hours, day, opening))}
                >
                  <Icon name="plus" size={16} />
                </Button>
              </span>
            )}
          </li>
        )
      })}
    </ul>
  )
}
